import type { ReactElement } from 'react';
import { Link } from 'react-router';

import {
  type ConsentTerm,
  type ConsentType,
  OPTIONAL_CONSENT_TERMS,
  REQUIRED_CONSENT_TERMS,
} from '../model/consent.schema';

type ConsentAgreementProps = {
  granted: ReadonlySet<ConsentType>;
  onChange: (next: ReadonlySet<ConsentType>) => void;
  /** 같은 화면에 두 번 놓일 수 있어 id 를 분리한다 */
  idPrefix: string;
};

const checkboxClass = 'size-4 shrink-0 accent-primary';

/** 항상 새 Set 을 만든다 — 기존 Set 을 변형하면 리렌더가 안 걸린다. */
function toggle(granted: ReadonlySet<ConsentType>, type: ConsentType): ReadonlySet<ConsentType> {
  const next = new Set(granted);
  if (next.has(type)) next.delete(type);
  else next.add(type);
  return next;
}

function DetailLink({ term }: { term: ConsentTerm }): ReactElement | null {
  if (!term.href) return null;

  return (
    <Link
      to={term.href}
      target="_blank"
      rel="noreferrer"
      className="shrink-0 text-[12px] text-muted-foreground underline underline-offset-2"
    >
      보기
    </Link>
  );
}

/**
 * 동의 블록 — 14. 1차 오픈 범위 §5.4.
 *
 * 「전체 동의」가 선택 항목까지 켜면 사용자가 인지하지 못한 마케팅 동의가 기록된다.
 * 그래서 전체 동의는 **필수 항목만** 덮고, 선택 항목은 구분선 아래에 독립으로 둔다.
 * 선택 항목은 접지 않는다 — 접어 두면 아무도 안 편다.
 */
export function ConsentAgreement({
  granted,
  onChange,
  idPrefix,
}: ConsentAgreementProps): ReactElement {
  const allRequiredChecked = REQUIRED_CONSENT_TERMS.every((term) => granted.has(term.type));

  const toggleAllRequired = () => {
    const next = new Set(granted);
    for (const term of REQUIRED_CONSENT_TERMS) {
      if (allRequiredChecked) next.delete(term.type);
      else next.add(term.type);
    }
    onChange(next);
  };

  return (
    <fieldset className="flex flex-col gap-3 rounded-md border border-border p-4">
      <legend className="sr-only">약관 동의</legend>

      <label className="flex items-center gap-2 border-border border-b pb-3 text-[14px] font-medium">
        <input
          type="checkbox"
          className={checkboxClass}
          checked={allRequiredChecked}
          onChange={toggleAllRequired}
        />
        필수 항목에 모두 동의
      </label>

      <div className="flex flex-col gap-2">
        {REQUIRED_CONSENT_TERMS.map((term) => (
          <div key={term.type} className="flex items-center justify-between gap-2">
            <label
              htmlFor={`${idPrefix}-${term.type}`}
              className="flex items-center gap-2 text-[13px]"
            >
              <input
                id={`${idPrefix}-${term.type}`}
                type="checkbox"
                className={checkboxClass}
                checked={granted.has(term.type)}
                onChange={() => onChange(toggle(granted, term.type))}
              />
              <span>
                <span className="text-muted-foreground">[필수]</span> {term.label}
              </span>
            </label>
            <DetailLink term={term} />
          </div>
        ))}
      </div>

      {OPTIONAL_CONSENT_TERMS.map((term) => (
        <div
          key={term.type}
          className="flex flex-col gap-2 border-border border-t-2 pt-3"
          data-optional-consent=""
        >
          <div className="flex items-start justify-between gap-2">
            <label
              htmlFor={`${idPrefix}-${term.type}`}
              className="flex items-start gap-2 text-[13px]"
            >
              <input
                id={`${idPrefix}-${term.type}`}
                type="checkbox"
                className={`${checkboxClass} mt-0.5`}
                checked={granted.has(term.type)}
                onChange={() => onChange(toggle(granted, term.type))}
              />
              <span>
                <span className="text-muted-foreground">[선택]</span> {term.label}
              </span>
            </label>
            <DetailLink term={term} />
          </div>

          {term.notices ? (
            <dl className="flex flex-col gap-1 rounded-sm bg-secondary px-3 py-2 text-[12px] text-muted-foreground">
              {term.notices.map((notice) => (
                <div key={notice.term} className="flex gap-2">
                  <dt className="w-[4.5rem] shrink-0">· {notice.term}</dt>
                  <dd>{notice.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}

          {term.noPenaltyNote ? (
            <p className="text-[12px] font-medium text-foreground">{term.noPenaltyNote}</p>
          ) : null}
        </div>
      ))}
    </fieldset>
  );
}
